/**
 * Curated city list for job-location entry.
 *
 * Why a curated list and not `talexo_city_master`: that table has ~43,000 rows
 * but it is a post-office / sub-locality dataset, not a city list — it has no
 * row for "Bangalore" or "Pune" at all. Using it to validate locations would
 * reject the most common entries outright.
 *
 * Canonical spellings deliberately match the FIRST name in each alias group in
 * the backend's utils/locationMatcher.js (mumbai|bombay, bangalore|bengaluru,
 * chennai|madras, kolkata|calcutta), so a value picked here is already in the
 * form the matcher and the facet aggregator expect.
 *
 * Free text is what produced the current mess: 27 distinct values across only
 * 102 jobs — "Delhi" (50), "New Delhi" (15), "Delhi,india" (2), "janakpuri",
 * plus states ("Himachal", "Bihar") and non-places ("Remote") sitting in the
 * city facet. Selecting from this list is what stops that recurring.
 */

/**
 * State → the cities offered under it. The single source of truth for both
 * dropdowns on the job form.
 *
 * Grouped by state rather than kept as a flat list so that adding a city is one
 * edit in the obvious place, and so a city cannot exist without a state — which
 * is what makes the State dropdown safe to derive rather than maintain twice.
 *
 * Why not `talexo_city_master`, which has 42,964 rows and a real `state_id`
 * hierarchy: it is a village and post-office dataset, not a city list. Checked
 * against every location currently on a live job, it has no row for Delhi,
 * Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Pune, Ahmedabad, Jaipur or
 * Lucknow — 19 of the 20 values in use are absent. The one that matched, Mandi,
 * matched a village in Haryana rather than the town in Himachal. It also holds
 * 876 city names that appear in more than one state, and 10,492 rows for Andhra
 * Pradesh alone, so it could not populate a usable dropdown even if the cities
 * were there.
 *
 * `talexo_state_master` (36 rows) is sound and could supply the State list, but
 * 5 of its states have no selectable city here, and offering a state that leads
 * to an empty city list is a dead end. Note if it is ever joined to: it spells
 * Chhattisgarh "Chattisgarh", calls Puducherry "Pondicherry", truncates
 * "Andaman and Nico.In.", and predates Ladakh.
 *
 * Every state and union territory is represented, so no recruiter is blocked.
 */
export const STATE_CITIES = {
  'Andaman and Nicobar Islands': ['Port Blair'],
  'Andhra Pradesh': ['Kurnool', 'Nellore', 'Tirupati', 'Vijayawada', 'Visakhapatnam'],
  'Arunachal Pradesh': ['Itanagar'],
  Assam: ['Dispur', 'Guwahati', 'Tinsukia'],
  Bihar: ['Gaya', 'Patna'],
  Chandigarh: ['Chandigarh'],
  Chhattisgarh: ['Bhilai', 'Raipur'],
  'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa'],
  Delhi: ['Delhi', 'New Delhi'],
  Goa: ['Goa', 'Panaji'],
  Gujarat: ['Ahmedabad', 'Bhavnagar', 'Gandhinagar', 'Rajkot', 'Surat', 'Vadodara'],
  Haryana: [
      'Ambala', 'Bahadurgarh', 'Ballabhgarh', 'Bhiwani', 'Charkhi Dadri', 'Ellenabad',
      'Faridabad', 'Gohana', 'Gurugram', 'Hisar', 'Karnal', 'Kurukshetra', 'Mahendragarh',
      'Mandi Adampur', 'Manesar', 'Naraingarh', 'Palwal', 'Panchkula', 'Panipat', 'Rewari',
      'Rohtak', 'Sadhaura', 'Sirsa', 'Sonipat',
  ],
  'Himachal Pradesh': ['Dharamshala', 'Kullu', 'Mandi', 'Shimla', 'Solan'],
  'Jammu and Kashmir': ['Doda', 'Jammu', 'Srinagar'],
  Jharkhand: ['Dhanbad', 'Jamshedpur', 'Ranchi'],
  Karnataka: ['Bangalore', 'Belgaum', 'Hubli', 'Mangalore', 'Mysore'],
  Kerala: ['Kannur', 'Kochi', 'Kollam', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur'],
  Ladakh: ['Leh'],
  Lakshadweep: ['Kavaratti'],
  'Madhya Pradesh': [
      'Bhopal', 'Datia', 'Gwalior', 'Indore', 'Jabalpur', 'Karera', 'Satna', 'Ujjain',
  ],
  Maharashtra: [
      'Akola', 'Amravati', 'Aurangabad', 'Jalgaon', 'Kolhapur', 'Mumbai', 'Nagpur', 'Nanded',
      'Nashik', 'Navi Mumbai', 'Pune', 'Solapur', 'Thane', 'Vasai-Virar',
  ],
  Manipur: ['Imphal'],
  Meghalaya: ['Shillong'],
  Mizoram: ['Aizawl'],
  Nagaland: ['Kohima'],
  Odisha: ['Bhubaneswar', 'Cuttack', 'Rourkela'],
  Puducherry: ['Puducherry'],
  Punjab: ['Amritsar', 'Jalandhar', 'Ludhiana', 'Mohali', 'Patiala', 'Zirakpur'],
  Rajasthan: [
      'Ajmer', 'Bharatpur', 'Bikaner', 'Jaipur', 'Jodhpur', 'Kota', 'Pilani', 'Sri Ganganagar',
      'Suratgarh', 'Udaipur',
  ],
  Sikkim: ['Gangtok'],
  'Tamil Nadu': [
      'Ambattur', 'Chennai', 'Coimbatore', 'Erode', 'Madurai', 'Salem', 'Tiruchirappalli',
      'Tiruppur', 'Virudhunagar',
  ],
  Telangana: ['Hyderabad', 'Warangal'],
  Tripura: ['Agartala'],
  'Uttar Pradesh': [
      'Agra', 'Aligarh', 'Allahabad', 'Baghpat', 'Bareilly', 'Budaun', 'Bulandshahr',
      'Chitrakoot', 'Etawah', 'Firozabad', 'Ghaziabad', 'Gorakhpur', 'Greater Noida', 'Hapur',
      'Jaunpur', 'Jhansi', 'Kanpur', 'Kushinagar', 'Lalganj', 'Loni', 'Lucknow', 'Mainpuri',
      'Mathura', 'Meerut', 'Moradabad', 'Muzaffarnagar', 'Nichlaul', 'Noida', 'Pilibhit',
      'Pratapgarh', 'Rampur', 'Saharanpur', 'Unnao', 'Varanasi',
  ],
  Uttarakhand: [
      'Almora', 'Dehradun', 'Haldwani', 'Haridwar', 'Pithoragarh', 'Rishikesh', 'Roorkee',
      'Rudrapur',
  ],
  'West Bengal': ['Asansol', 'Durgapur', 'Howrah', 'Kolkata', 'Siliguri'],
};

/** States offered in the picker, alphabetically. Every one has at least one city. */
export const INDIAN_STATES = Object.keys(STATE_CITIES).sort((a, b) => a.localeCompare(b));

/**
 * City → its state. Derived, so the two can never disagree.
 *
 * Also what lets the edit form fill in the State for a job saved before these
 * fields existed: those rows hold only a city, and the state follows from it.
 */
export const CITY_STATE = Object.entries(STATE_CITIES).reduce((acc, [state, cities]) => {
  cities.forEach((city) => { acc[city] = state; });
  return acc;
}, {});

/**
 * Cities offered in the picker: metros, every state and UT capital, major tier-2.
 * Derived from STATE_CITIES so a city always has a state behind it.
 */
export const INDIAN_CITIES = Object.keys(CITY_STATE).sort((a, b) => a.localeCompare(b));

/** Cities in one state, alphabetically. Empty array for an unknown state. */
export const citiesInState = (state) => {
  const cities = STATE_CITIES[state];
  return cities ? [...cities].sort((a, b) => a.localeCompare(b)) : [];
};

/** The state a city belongs to, or null when the city is not in the list. */
export const stateForCity = (city) => CITY_STATE[city] || null;

/**
 * Legacy free-text → canonical city. Keys are compared lowercase/trimmed.
 *
 * NOTE New Delhi is deliberately NOT here. It is the NDMC municipality, a
 * distinct city inside the NCT of Delhi, so it belongs in STATE_CITIES and is
 * stored and displayed as itself. Search does not fragment as a result: the
 * backend keeps ['delhi','new delhi'] in LOCATION_ALIASES, so filtering either
 * one finds both, and the job facet counts them under a single Delhi bucket.
 *
 * Two jobs to do: fold spelling and sub-locality variants onto one city, and
 * give the edit form something to preselect when it loads an old job whose
 * location was typed by hand. The same map is what a data-cleanup script
 * should use so the DB and the UI agree on canonical names.
 */
export const CITY_ALIASES = {
  'delhi,india': 'Delhi',
  'delhi, india': 'Delhi',
  'delhi india': 'Delhi',
  'janakpuri': 'Delhi',
  'janakpuri delhi': 'Delhi',
  'dwarka': 'Delhi',
  'rohini': 'Delhi',
  'saket': 'Delhi',
  // South Delhi locality, like dwarka/rohini/saket above. Note this claims
  // the name for Delhi, so Chhatarpur city in Madhya Pradesh would also
  // resolve here — it has no row in this data and no entry in STATE_CITIES.
  'chattarpur': 'Delhi',
  'chhatarpur': 'Delhi',
  'bengaluru': 'Bangalore',
  'banglore': 'Bangalore',
  'bangaluru': 'Bangalore',
  'bombay': 'Mumbai',
  'madras': 'Chennai',
  'calcutta': 'Kolkata',
  'gurgaon': 'Gurugram',
  'trivandrum': 'Thiruvananthapuram',
  'cochin': 'Kochi',
  'calicut': 'Kozhikode',
  'pondicherry': 'Puducherry',
  'vizag': 'Visakhapatnam',
  'trichy': 'Tiruchirappalli',
  'baroda': 'Vadodara',
  'mysuru': 'Mysore',
  'prayagraj': 'Allahabad',
  'noida extension': 'Greater Noida',
  'sriganganagar': 'Sri Ganganagar',
  'hubli-dharwad': 'Hubli',
  'ballabgarh': 'Ballabhgarh',
};

/** Lowercased city → canonical, for exact-match resolution. */
const CANONICAL_BY_LOWER = INDIAN_CITIES.reduce((acc, city) => {
  acc[city.toLowerCase()] = city;
  return acc;
}, {});

/**
 * Resolve any historical location string to a canonical city, or null when it
 * isn't a city at all (states like "Himachal", work modes like "Remote").
 * Callers decide what to do with null — the form leaves the field empty so the
 * recruiter has to pick, rather than silently inventing a value.
 */
export function toCanonicalCity(raw) {
  if (!raw || typeof raw !== 'string') return null;
  // Historical values arrive as "Delhi,india" / "Bangalore, Karnataka" — the
  // city is the first comma-separated part.
  const head = raw.split(',')[0].trim().toLowerCase();
  if (!head) return null;
  if (CANONICAL_BY_LOWER[head]) return CANONICAL_BY_LOWER[head];
  if (CITY_ALIASES[head]) return CITY_ALIASES[head];
  // Try the untrimmed whole string too, for keys like "delhi, india".
  const whole = raw.trim().toLowerCase();
  return CITY_ALIASES[whole] || null;
}
